import React from "react";
import styled from "@emotion/styled";
import moment from "moment";
import { UserContext, ThemeContext, LanguageContext } from "../contexts/Context";
import CheckoutPanel from "./CheckoutPanel";
import { lightTint, readableText } from "../utils/themeContrast";

export default function OrderReceipt({ items = [], customerName, phone, address, scheduleTime, deliveryType, takeAwayId, hideIdentity = false, tableLabel}) {
  const user = React.useContext(UserContext);
  const theme = React.useContext(ThemeContext);
  const languages = React.useContext(LanguageContext);
  const labels = languages[user.lanIndex] || {};
  const chinese = user.lanIndex == 1;
  const service = deliveryType == 2 ? (labels.delivery || "Delivery") : user.isTakeAway ? (labels.pickUp || "Pick-up") : (labels.dineIn || "Dine-in");
  const ordered = user.orderTime ? moment(user.orderTime) : null;
  const scheduled = scheduleTime ? moment(scheduleTime) : null;
  const rows = [
    [labels.name || "Name", customerName],
    [service, scheduled && scheduled.isValid() ? scheduled.format("D MMM YYYY, h:mm A") : null],
    [labels.guest || "Guests", !user.isTakeAway && user.pax],
    [labels.phone || "Phone", phone],
    [labels.address || "Address", address],
    [chinese ? "\u4e0b\u5355\u65f6\u95f4" : "Ordered", ordered && ordered.isValid() ? ordered.format("h:mm A") : null],
    [chinese ? "\u65e5\u671f" : "Date", ordered && ordered.isValid() ? ordered.format("D MMM YYYY") : null]
  ];
  return <Receipt themeContext={theme}>
    {!hideIdentity && <OrderIdentity takeAwayId={takeAwayId} tableLabel={tableLabel}/>}
    <Business><strong>{user.company && (user.company.shortName || user.company.companyName)}</strong><small>{service}</small></Business>
    <Details>{rows.filter(([, value]) => value !== null && value !== undefined && value !== false && value !== "").map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</Details>
    <Items>
      <Caption><span>{chinese ? "\u8ba2\u5355\u5546\u54c1" : "Order items"}</span><span>{items.reduce((total, item) => total + Number(item.qty || 0), 0)} {chinese ? "\u4ef6" : "items"}</span></Caption>
      {items.map((item, index) => <CheckoutPanel key={item.codeId + ":" + index} item={item} receipt lanIndex={user.lanIndex} isLanReverse={user.isLanReverse} currencySymbol={user.currencySymbol} />)}
    </Items>
  </Receipt>;
}
const Receipt = styled.section`
  padding: 0 16px 12px;
  color: ${p => readableText(p.themeContext.backgroundColor, p.themeContext.fontPrimary)};
  font-family: ${p => p.themeContext.fontFamily};
`;
const Business = styled.div`
  text-align: center; padding: 18px 0 14px;
  strong { display: block; font-size: 15px; line-height: 1.4; }
  small { display: block; margin-top: 4px; font-size: 11px; opacity: .75; }
`;
const Details = styled.dl`
  margin: 0 0 10px; padding-bottom: 12px; border-bottom: 1px dashed rgba(128,128,128,.22);
  > div { display: flex; gap: 16px; justify-content: space-between; margin-top: 6px; font-size: 12px; line-height: 1.35; }
  dt { opacity: .75; flex-shrink: 0; } dd { margin: 0; text-align: right; overflow-wrap: anywhere; }
`;
const Items = styled.div`
  background: rgba(128,128,145,.13); border: 1px solid rgba(128,128,145,.1); border-radius: 14px; padding: 12px 11px 0;
`;
const Caption = styled.div`
  display: flex; justify-content: space-between; gap: 12px; font-size: 10px; text-transform: uppercase; letter-spacing: .4px; opacity: .8; padding: 2px 0 4px;
`;

export function OrderIdentity({ takeAwayId, tableLabel }) {
  const user = React.useContext(UserContext);
  const theme = React.useContext(ThemeContext);
  const languages = React.useContext(LanguageContext);
  const labels = languages[user.lanIndex] || {};
  const tableOrder = !user.isTakeAway || user.isInRoom || user.tkType == 1;
  const table = tableLabel ? tableLabel : tableOrder && user.tableLabel;
  const id = table || takeAwayId;
  if (!id) return null;
  const label = table ? (user.isInRoom ? (labels.roomService || "Room") : (labels.table || "Table")) : (user.lanIndex == 1 ? "\u5916\u5e26\u5355\u53f7" : "Takeaway ID");
  return <Identity themeContext={theme}><span>{label}</span><strong>{id}</strong>{table && takeAwayId && <small>{labels.yourOrderNo || "Order no."}: {takeAwayId}</small>}</Identity>;
}
const Identity = styled.div`
  margin: 16px 0 0;
  padding: 16px;
  border-radius: 14px;
  text-align: center;
  background: ${p => lightTint(p.themeContext.primaryColor, .09)};
  color: ${p => readableText(lightTint(p.themeContext.primaryColor, .09), p.themeContext.primaryColor)};
  span { display: block; font-size: 13px; font-weight: 600; }
  strong { display: block; margin-top: 4px; font-size: 34px; font-weight: 750; line-height: 1.2; overflow-wrap: anywhere; }
  small { display: block; margin-top: 8px; font-size: 12px; }
`;
